import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { fillUnitPrice } from "../utils/productPrice";
import UnitPriceInput from "./UnitPriceInput";
import { isLockedDate, useTransactionLockDate } from "../utils/transactionLock";
import ImportExportBar from "./ImportExportBar";
import Pagination from "./Pagination";

const PAGE_SIZE = 20;

const emptyItem = {
  productId: "",
  quantity: "",
  unitPrice: "",
  discountAmount: "0",
  taxAmount: "0",
  distributorMarginPercent: "",
  batchNumber: "",
  expiryDate: "",
};
// paymentTerm: PAYMENT (paid on purchase) or CREDIT (to be paid by creditDueDate; the distributor is reminded the day before)
const emptyForm = { brandId: "", brandInvoiceNumber: "", invoiceDate: "", paymentTerm: "PAYMENT", creditDueDate: "", items: [{ ...emptyItem }] };

const statusBadgeClass = {
  IMPORTED: "badge-amber",
  VERIFIED: "badge-green",
  CANCELLED: "badge-red",
};

const money = (value) =>
  value === null || value === undefined || value === "" ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const right = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

const lineTotal = (item) =>
  (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) - (Number(item.discountAmount) || 0) + (Number(item.taxAmount) || 0);

const PurchaseInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const lockDate = useTransactionLockDate();
  const [brandFilter, setBrandFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const brandById = useMemo(() => {
    const map = new Map();
    brands.forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);

  const productById = useMemo(() => {
    const map = new Map();
    products.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [products]);

  const loadOptions = async () => {
    try {
      const [brandsData, productsData] = await Promise.all([
        api.get("/api/v1/brands", { size: 200 }),
        api.get("/api/v1/products", { size: 1000 }),
      ]);
      setBrands(brandsData?.content || []);
      setProducts(productsData?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load brands / products");
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/purchase-invoices", {
        page,
        size: PAGE_SIZE,
        brandId: brandFilter || undefined,
        status: statusFilter || undefined,
      });
      setInvoices(data?.content || []);
      setTotalPages(data?.totalPages ?? 0);
      setTotalElements(data?.totalElements ?? 0);
    } catch (error) {
      toast.error(error.message || "Failed to load purchase invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  // Changing a filter should always jump back to page 0 — otherwise a narrower result set can land you past
  // its last page, silently showing nothing.
  useEffect(() => {
    setPage(0);
  }, [brandFilter, statusFilter]);

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandFilter, statusFilter, page]);

  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allExpanded = invoices.length > 0 && invoices.every((invoice) => expandedIds.has(invoice.id));
  const toggleAllExpanded = () => setExpandedIds(allExpanded ? new Set() : new Set(invoices.map((invoice) => invoice.id)));

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  // Only an invoice that is not verified yet can be edited: verifying puts its stock into inventory.
  const openEdit = (invoice) => {
    setEditingId(invoice.id);
    setFormData({
      brandId: String(invoice.brandId),
      brandInvoiceNumber: invoice.brandInvoiceNumber || "",
      invoiceDate: invoice.invoiceDate || "",
      paymentTerm: invoice.paymentTerm || "PAYMENT",
      creditDueDate: invoice.creditDueDate || "",
      items: invoice.items.map((item) => ({
        productId: String(item.productId),
        quantity: String(item.quantity ?? ""),
        unitPrice: String(item.unitPrice ?? ""),
        discountAmount: String(item.discountAmount ?? "0"),
        taxAmount: String(item.taxAmount ?? "0"),
        distributorMarginPercent: item.distributorMarginPercent ?? "",
        batchNumber: item.batchNumber || "",
        expiryDate: item.expiryDate || "",
        // shown when the product is not among the loaded products (e.g. it was deactivated)
        productLabel: `${item.productName} (${item.skuCode})`,
      })),
    });
    setShowModal(true);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      // a newly picked product brings its own prices and distributor margin
      if (field === "productId") {
        items[index].unitPrice = "";
        items[index].priceOptions = [];
        items[index].distributorMarginPercent = productById.get(String(value))?.distributorMarginPercent ?? "";
      }
      return { ...prev, items };
    });
    if (field === "productId") fillUnitPrice(setFormData, index, value, "purchase");
  };

  const addItemRow = () => {
    setFormData((prev) => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  };

  const removeItemRow = (index) => {
    setFormData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const totalAmount = formData.items.reduce((sum, item) => sum + lineTotal(item), 0);

  // products of the invoice's brand only: the backend rejects a product of another brand
  const brandProducts = formData.brandId ? products.filter((p) => String(p.brandId) === String(formData.brandId)) : products;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.brandId) {
      toast.error("Please select a brand");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        brandId: Number(formData.brandId),
        brandInvoiceNumber: formData.brandInvoiceNumber,
        invoiceDate: formData.invoiceDate,
        paymentTerm: formData.paymentTerm,
        creditDueDate: formData.paymentTerm === "CREDIT" ? formData.creditDueDate || null : null,
        totalAmount: Math.round(totalAmount * 100) / 100,
        items: formData.items.map((item) => ({
          productId: Number(item.productId),
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          discountAmount: Number(item.discountAmount) || 0,
          taxAmount: Number(item.taxAmount) || 0,
          totalAmount: Math.round(lineTotal(item) * 100) / 100,
          batchNumber: item.batchNumber || null,
          expiryDate: item.expiryDate || null,
          // blank = the product's own distributor margin
          distributorMarginPercent: item.distributorMarginPercent === "" ? null : Number(item.distributorMarginPercent),
        })),
      };
      if (editingId) {
        await api.put(`/api/v1/purchase-invoices/${editingId}`, payload);
        toast.success("Purchase invoice updated");
      } else {
        await api.post("/api/v1/purchase-invoices", payload);
        toast.success("Purchase invoice created");
      }
      setShowModal(false);
      loadInvoices();
    } catch (error) {
      toast.error(error.message || "Failed to save purchase invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (id) => {
    setVerifyingId(id);
    try {
      await api.post(`/api/v1/purchase-invoices/${id}/verify`);
      toast.success("Purchase invoice verified — stock posted");
      loadInvoices();
    } catch (error) {
      toast.error(error.message || "Failed to verify purchase invoice");
    } finally {
      setVerifyingId(null);
    }
  };

  const sumOf = (items, field) => items.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading purchase invoices...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
          <option value="">All brands</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="IMPORTED">Imported</option>
          <option value="VERIFIED">Verified</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <button className="primary" onClick={openCreate}>
          + New Purchase Invoice
        </button>
        <button className="secondary" onClick={toggleAllExpanded} disabled={invoices.length === 0}>
          {allExpanded ? "Hide all items" : "Show all items"}
        </button>
        <ImportExportBar
          importPath={brandFilter ? "/api/v1/purchase-invoices/import" : undefined}
          exportPath="/api/v1/purchase-invoices/export"
          exportFilename="purchase-invoices.xlsx"
          extraParams={{ brandId: brandFilter || undefined, status: statusFilter || undefined }}
          label="Invoices"
          onImported={loadInvoices}
        />
        {!brandFilter ? (
          <span className="page-subtitle">Select a brand above to enable Excel import</span>
        ) : null}
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Brand</th>
              <th>Brand Invoice #</th>
              <th>Date</th>
              <th>Items</th>
              <th style={right}>Total Amount</th>
              <th style={right}>Paid</th>
              <th style={right}>Outstanding</th>
              <th>Payment / Credit</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={11}>No purchase invoices found.</td>
              </tr>
            ) : (
              invoices.map((invoice) => {
                const locked = isLockedDate(invoice.invoiceDate, lockDate);
                const expanded = expandedIds.has(invoice.id);
                const items = invoice.items || [];
                return (
                  <Fragment key={invoice.id}>
                    <tr>
                      <td>{invoice.id}</td>
                      <td>{invoice.brandName || brandById.get(invoice.brandId) || invoice.brandId}</td>
                      <td>{invoice.brandInvoiceNumber}</td>
                      <td>{invoice.invoiceDate}</td>
                      <td>{items.length}</td>
                      <td style={right}>{money(invoice.totalAmount)}</td>
                      <td style={right}>{money(invoice.amountPaid)}</td>
                      <td style={right}>{money(invoice.outstanding)}</td>
                      <td>
                        {invoice.paymentTerm === "CREDIT" ? (
                          <span className="badge badge-amber" title="Bought on credit">
                            Credit{invoice.creditDueDate ? ` till ${invoice.creditDueDate}` : ""}
                          </span>
                        ) : (
                          <span className="badge badge-green">Payment</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusBadgeClass[invoice.status] || "badge-gray"}`}>{invoice.status}</span>
                      </td>
                      <td className="action-buttons">
                        <button className="secondary" onClick={() => toggleExpanded(invoice.id)}>
                          {expanded ? "Hide items" : "View items"}
                        </button>
                        {locked ? (
                          <span className="badge badge-gray" title={`Dated on or before the transaction lock date (${lockDate}); it can no longer be changed`}>
                            Locked
                          </span>
                        ) : invoice.status === "IMPORTED" ? (
                          <>
                            <button className="secondary" onClick={() => openEdit(invoice)}>
                              Edit
                            </button>
                            <button className="primary" onClick={() => handleVerify(invoice.id)} disabled={verifyingId === invoice.id}>
                              {verifyingId === invoice.id ? "Verifying..." : "Verify"}
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td colSpan={10} style={{ background: "var(--color-surface-alt, #f8fafc)", padding: "10px 14px" }}>
                          <table style={{ width: "100%" }}>
                            <thead>
                              <tr>
                                <th>SKU</th>
                                <th style={{ textAlign: "left" }}>Item</th>
                                <th style={right}>Qty</th>
                                <th>UOM</th>
                                <th style={right}>Unit Price</th>
                                <th style={right}>Discount</th>
                                <th style={right}>Tax</th>
                                <th style={right}>Dist. Margin %</th>
                                <th style={right}>Line Total</th>
                                <th>Batch #</th>
                                <th>Expiry</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.length === 0 ? (
                                <tr>
                                  <td colSpan={11}>This invoice has no line items.</td>
                                </tr>
                              ) : (
                                items.map((item) => (
                                  <tr key={item.id}>
                                    <td>{item.skuCode}</td>
                                    <td style={{ textAlign: "left" }}>{item.productName}</td>
                                    <td style={right}>{qty(item.quantity)}</td>
                                    <td>{item.uom || "-"}</td>
                                    <td style={right}>{money(item.unitPrice)}</td>
                                    <td style={right}>{money(item.discountAmount)}</td>
                                    <td style={right}>{money(item.taxAmount)}</td>
                                    <td style={right}>{item.distributorMarginPercent ?? "-"}</td>
                                    <td style={right}>{money(item.totalAmount)}</td>
                                    <td>{item.batchNumber || "-"}</td>
                                    <td>{item.expiryDate || "-"}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
          {invoices.length ? (
            <tfoot>
              <tr>
                <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
                  Total of {invoices.length} invoice(s) on this page
                </td>
                <td style={{ ...right, fontWeight: 700 }}>{money(invoices.reduce((sum, inv) => sum + Number(inv.totalAmount || 0), 0))}</td>
                <td style={{ ...right, fontWeight: 700 }}>{money(invoices.reduce((sum, inv) => sum + Number(inv.amountPaid || 0), 0))}</td>
                <td style={{ ...right, fontWeight: 700 }}>{money(invoices.reduce((sum, inv) => sum + Number(inv.outstanding || 0), 0))}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          ) : null}
                            {items.length > 0 ? (
                              <tfoot>
                                <tr>
                                  <td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>
                                    Total
                                  </td>
                                  <td style={{ ...right, fontWeight: 700 }}>{qty(sumOf(items, "quantity"))}</td>
                                  <td />
                                  <td />
                                  <td style={{ ...right, fontWeight: 700 }}>{money(sumOf(items, "discountAmount"))}</td>
                                  <td style={{ ...right, fontWeight: 700 }}>{money(sumOf(items, "taxAmount"))}</td>
                                  <td />
                                  <td style={{ ...right, fontWeight: 700 }}>{money(sumOf(items, "totalAmount"))}</td>
                                  <td colSpan={2} />
                                </tr>
                              </tfoot>
                            ) : null}
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} />

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box modal-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Purchase Invoice" : "Create Purchase Invoice"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Brand{editingId ? " (cannot be changed)" : ""}</label>
                  <select name="brandId" value={formData.brandId} onChange={handleFieldChange} required disabled={Boolean(editingId)}>
                    <option value="">Select brand</option>
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Brand Invoice Number</label>
                  <input name="brandInvoiceNumber" value={formData.brandInvoiceNumber} onChange={handleFieldChange} required />
                </div>
                <div className="form-group">
                  <label>Invoice Date</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="invoiceDate" value={formData.invoiceDate} onChange={handleFieldChange} required />
                </div>
                <div className="form-group">
                  <label>Total Amount (auto)</label>
                  <input value={totalAmount.toFixed(2)} disabled />
                </div>
                <div className="form-group">
                  <label>Payment or Credit</label>
                  <select name="paymentTerm" value={formData.paymentTerm} onChange={handleFieldChange}>
                    <option value="PAYMENT">Payment (paid on purchase)</option>
                    <option value="CREDIT">Credit (pay later)</option>
                  </select>
                </div>
                {formData.paymentTerm === "CREDIT" ? (
                  <div className="form-group">
                    <label>Credit Date (payment due)</label>
                    <input type="date" onClick={(e) => e.target.showPicker?.()} name="creditDueDate" value={formData.creditDueDate}
                      onChange={handleFieldChange} required />
                    <div className="page-subtitle">An SMS reminder and an alert go to the distributor one day before.</div>
                  </div>
                ) : null}
              </div>

              <h4 style={{ marginTop: "20px", marginBottom: "8px", fontWeight: 600 }}>Items</h4>
              <div className="table-wrapper">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                      <th title="Distributor margin %; filled from the product, can be changed for this invoice">Dist. Margin %</th>
                      <th>Line Total</th>
                      <th>Batch #</th>
                      <th>Expiry</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <select
                            value={item.productId}
                            onChange={(e) => handleItemChange(index, "productId", e.target.value)}
                            required
                          >
                            <option value="">Select product</option>
                            {item.productId && !brandProducts.some((p) => String(p.id) === String(item.productId)) ? (
                              <option value={item.productId}>{item.productLabel || `Product #${item.productId}`}</option>
                            ) : null}
                            {brandProducts.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} ({p.skuCode})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            style={{ width: "90px" }}
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <UnitPriceInput
                            value={item.unitPrice}
                            onChange={(value) => handleItemChange(index, "unitPrice", value)}
                            options={item.priceOptions}
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={{ width: "90px" }}
                            value={item.discountAmount}
                            onChange={(e) => handleItemChange(index, "discountAmount", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            style={{ width: "90px" }}
                            value={item.taxAmount}
                            onChange={(e) => handleItemChange(index, "taxAmount", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.001"
                            style={{ width: "80px" }}
                            value={item.distributorMarginPercent}
                            onChange={(e) => handleItemChange(index, "distributorMarginPercent", e.target.value)}
                            aria-label="Distributor margin percent"
                          />
                        </td>
                        <td style={right}>{money(lineTotal(item))}</td>
                        <td>
                          <input
                            style={{ width: "100px" }}
                            value={item.batchNumber}
                            onChange={(e) => handleItemChange(index, "batchNumber", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            onClick={(e) => e.target.showPicker?.()}
                            value={item.expiryDate}
                            onChange={(e) => handleItemChange(index, "expiryDate", e.target.value)}
                          />
                        </td>
                        <td>
                          {formData.items.length > 1 ? (
                            <button type="button" className="close-btn" onClick={() => removeItemRow(index)}>
                              &times;
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="secondary" style={{ marginTop: "10px" }} onClick={addItemRow}>
                + Add Item
              </button>

              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PurchaseInvoices;
